<template>
  <div>
    <el-form
        ref="search"
        :model="search"
        status-icon
        @submit.native.prevent="submitForm">
      <el-form-item
          :rules="searchRules.criteria"
          prop="criteria">
        <el-input
            v-if="searching"
            size="medium"
            class="nav-search"
            :placeholder="searchingPlaceholder"
            prefix-icon="el-icon-loading" />
        <el-input
            v-else
            v-model.trim="search.criteria"
            size="medium"
            type="text"
            placeholder="Search"
            prefix-icon="el-icon-search"
            class="nav-search"
            auto-complete="on" />
      </el-form-item>
    </el-form>
    <ProfileCreator
        v-if="openProfileCreatorModal"
        :device-id="search.criteria"
        :open.sync="openProfileCreatorModal" />
    <DeviceSelector
        v-if="openDeviceSelectorModal"
        :devices="deviceMatches"
        :profile="deviceProfile"
        :open.sync="openDeviceSelectorModal" />
    <RingSolutionsRedirectDialog
        v-if="result"
        :device="result"
        :open.sync="openRedirectToRingSolutions" />
    <OrderDetails
        v-if="detailsVisible"
        :visible.sync="detailsVisible"
        :order-number="order_id" />
  </div>
</template>

<script>
import { get, values } from "lodash";
import { EventBus } from "../../../../eventBus";
import { createNamespacedHelpers as module } from "vuex";
import searchMixin from "@/mixins/search";
const { mapActions: accountActions } = module("db/account");
const { mapActions: requestActions } = module("db/requests");
const { mapActions: sharedActions, mapMutations: sharedMutations } =
  module("shared");
import RingSolutionsRedirectDialog from "@/containers/Core/RingSolutionsRedirectDialog";
import ProfileCreator from "@/components/Shared/ProfileCreator";
import DeviceSelector from "@/components/Shared/DeviceSelector";
import axios from "axios";
import OrderDetails from "../../../../containers/Customer/OrderWidget/OrderDetails.vue";

export default {
  name: "MainSearch",
  components: {
    OrderDetails,
    RingSolutionsRedirectDialog,
    ProfileCreator,
    DeviceSelector,
  },
  mixins: [searchMixin],
  data() {
    let inferSearchType = (rule, value, callback) => {
      const type = this.getSearchEntity(value);
      if (type === "Unknown") {
        callback(
          new Error(
            "Use an e-mail, mac address, serial number, order id or a PLC uuid/gid"
          )
        );
      } else callback();
    };
    return {
      search: {
        criteria: "",
      },
      searchRules: {
        criteria: [{ validator: inferSearchType, trigger: "change" }],
      },
      searching: false,
      result: null,
      notFound: false,
      openRedirectToRingSolutions: false,
      openProfileCreatorModal: false,
      openDeviceSelectorModal: false,
      deviceMatches: [],
      detailsVisible: false,
      order_id: "",
    };
  },
  computed: {
    searchType() {
      return this.getSearchEntity(this.search.criteria);
    },
    searchingPlaceholder() {
      return "searching for " + this.search.criteria;
    },
  },
  watch: {
    "search.criteria": function (val) {
      val = String(val).trim();
      if (val === "") {
        this.$refs.search.resetFields();
      }
    },
  },
  methods: {
    submitForm() {
      this.$refs.search.validate((valid) => {
        if (valid) {
          if (this.search.criteria) {
            this.performSearch();
          } else {
            this.$refs.search.resetFields();
          }
        } else {
          return false;
        }
      });
    },
    ...accountActions(["globalSearch"]),
    ...requestActions(["fetchRequestsBy"]),
    ...sharedMutations(["setPiiAuthorizationStatus"]),
    ...sharedActions(["fetchPIIAuthorizationStatus"]),
    performSearch() {
      if (this.search.criteria.trim()) {
        this.params = {
          model: this.searchType,
          criteria: this.search.criteria.trim(),
        };
        this.searching = true;
        if (this.searchType === "Customer") {
          this.loadCustomer();
        } else if (["Device", "Alarm", "DSN"].includes(this.searchType)) {
          this.loadDevice();
        } else if (this.searchType.match(/^plc-/)) {
          this.loadPLCRequest();
        } else if (this.searchType === "order") {
          this.loadOrder();
        }
      }
    },
    loadOrder() {
      let uri = `/v3/orders/details`;
      axios
        .get(uri, { params: { order_ids: [this.search.criteria] } })
        .then((response) => {
          this.order_id = this.search.criteria;
          this.detailsVisible = true;
        })
        .catch((error) => {
          this.$message({
            type: "error",
            message: `Order #${this.search.criteria} not found`,
          });
        })
        .finally(() => {
          this.searching = false;
        });
    },
    loadCustomer() {
      this.params.includes = [];
      this.params.do_nothing_if_same_id = true;
      this.globalSearch(this.params)
        .then(({ found, id, do_nothing }) => {
          if (do_nothing) {
            this.$message({
              message: "You are already looking at it",
              type: "success",
            });
            return;
          }
          if (found) {
            this.$router.push(`/customer/${id}/dashboard`);
            this.fetchPIIAuthorizationStatus()
              .then((response) => {
                this.setPiiAuthorizationStatus(
                  response.data.authorization.status
                );
              })
              .catch((error) => {
                console.error(
                  "Could not fetch Pii Authorization Status",
                  error
                );
                this.setPiiAuthorizationStatus("revoked");
              });
          } else
            this.$message({
              type: "error",
              message: `Can not load. ${this.searchType}`,
            });
        })
        .catch((error) => {
          this.$message({
            type: "error",
            message: `Can not load customer. ${error.data.message}`,
          });
        })
        .finally((_) => {
          this.searching = false;
        });
    },
    loadDevice() {
      const { criteria: id, _ } = this.params;
      let device_id = String(id).toLowerCase();
      let uri = `v3/devices/${device_id}?includes[]=setups_history`;
      axios
        .get(uri, { headers: { "X-CUSTOMER-AUTH": 0 } })
        .then((resp) => {
          let response = resp.data;
          if (response.meta["is_alarm_device"]) {
            this.openRedirectToRingSolutions = true;
            this.result = response.device;
            this.searching = false;
            return;
          } else if (response.meta.full_data) {
            let other_devices = response.other_devices || [];
            this.deviceProfile = response.profile;
            if (other_devices.length > 1) {
              this.deviceMatches = other_devices;
              this.openDeviceSelectorModal = true;
            } else {
              let currentDev = other_devices[0] || response.device;
              if (currentDev.deleted_at || currentDev.deactivated_at) {
                EventBus.$emit("device-profile-only", {
                  ...response.profile,
                  deleted_at: response.device.deleted_at || undefined,
                  deactivated_at: response.device.deactivated_at || undefined,
                });
              } else
                this.$router.push(
                  `/customer/${currentDev.owner_id}/devices/${currentDev.doorbot_id}`,
                  this.stopLoading, // onComplete
                  this.stopLoading // onAbort
                );
            }
          } else if (
            response.device.setups_history.length === 0 ||
            response.meta.profile_only
          ) {
            EventBus.$emit("device-profile-only", response.profile);
          } else if (response.meta.no_data) {
            this.$message({
              type: "error",
              message: `Device ${id} not found`,
            });
          }
        })
        .catch((error) => {
          this.$message({
            type: "error",
            message: error.data.message,
          });
          if (error.status === 404 && !error.data.meta.is_accessory) {
            this.openProfileCreatorModal = true;
          }
        })
        .finally(() => (this.searching = false));
    },
    loadPLCRequest() {
      let idMap = { "plc-uuid": "uuid", "plc-gid": "gid" };
      let idName = idMap[this.searchType];
      let pathToId = `[0][0].${idName}`;
      const onComplete = () => {};
      const onAbort = () => {
        this.$notify.warning({
          title: `Customer Request (${this.search.criteria})`,
          message: "Not Found",
          duration: 5000,
        });
      };
      let query = {};
      let id = this.search.criteria.trim();
      this.fetchRequestsBy({ id })
        .then((resp) => {
          if (resp) {
            // search by plc uuid/gid is case insensitive, Request page search is not.
            // hence, we get the plc-uuid|gid from the response
            query[this.searchType] = get(values(resp.requests), pathToId, null);
            this.$router.push(
              {
                path: `/customer/${resp.customer_id}/requests`,
                query,
              },
              onComplete,
              onAbort
            );
          }
        })
        .catch(onAbort)
        .finally(() => (this.searching = false));
    },
  },
};
</script>

<style lang="css" scoped>
.el-form-item {
  margin-bottom: 0px;
}
</style>

<style lang="scss">
@import "@/assets/scss/newui/fonts.scss";

/* Magnifying Glass styles */
#new-ring-main-header {
  //.nav-right > ul > li:nth-child(1) > div > form > div > div > div > span {
  .nav-right > ul li div > form > div > div > div > span {
    left: unset;
    right: 11px;
  }

  .el-input--prefix .el-input__inner {
    @include body__normal-regular();
    padding-left: 16px;
  }
  ::placeholder {
    color: rgba(66, 66, 66, 0.75);
    //opacity: 1;
  }
}

/* Input search error icons styles */
div.nav-right
  div.nav-search.el-input.el-input--medium.el-input--prefix
  > span.el-input__suffix {
  transform: translate(-10px);
}
</style>
